import { captureException, configureScope } from '@sentry/browser'
import Cookies from 'js-cookie'
import { v4 } from 'uuid'
// eslint-disable-next-line import/no-cycle
import { PLAYER_ERROR_CODES } from './components/VideoPlayer'
import webOSStorageManager from './Platform/WebOS/WebOSStorageManager'

export function removeDuplicatesByProperty(array, property) {
  if (!Array.isArray(array)) return array

  const uniqueMap = new Map()
  array.forEach((item) => {
    uniqueMap.set(item[property], item)
  })
  return Array.from(uniqueMap.values())
}

export function noOp() {
  // intentionally does nothing
}
export function isValidJSON(str) {
  try {
    JSON.parse(str)
    return true
  } catch (e) {
    return false
  }
}

// eslint-disable-next-line consistent-return
export function getJSON(str) {
  if (isValidJSON(str)) {
    return JSON.parse(str)
  }
}

export function truncate(n, useWordBoundary) {
  if (!this) return ''
  if (this.length <= n) {
    return this
  }
  const subString = this.substr(0, n - 1)

  return `${
    useWordBoundary
      ? subString.substr(0, subString.lastIndexOf(' '))
      : subString
  } ...`
}

export function addMore(n) {
  if (!this) return ''
  if (this.length <= n) {
    return ' '
  }
  return ' More'
}

export function secondsToTTS(input) {
  const d = Number(input)
  if (Number.isNaN(d) || input == null) {
    return ''
  }
  if (d < 1) {
    return '0 seconds long.'
  }
  const h = Math.floor(d / 3600)
  const m = Math.floor((d % 3600) / 60)
  const s = Math.floor((d % 3600) % 60)
  let hDisplay = null
  let mDisplay = null
  let sDisplay = null
  if (h > 0) {
    hDisplay = `${h} hour`
  }
  if (h > 1) {
    hDisplay += 's'
  }
  if (m > 0) {
    mDisplay = `${m} minute`
  }
  if (m > 1) {
    mDisplay += 's'
  }
  if (s > 0) {
    sDisplay = `${s} second`
  }
  if (s > 1) {
    sDisplay += 's'
  }
  const filtered = [hDisplay, mDisplay, sDisplay].filter((x) => x !== null)
  if (filtered.length > 1) {
    filtered.splice(filtered.length - 1, 0, 'and')
  }
  return `${filtered.join(' ')} long.`
}

export function secondsToHms(d) {
  let localD = d
  localD = Number(localD)
  const h = Math.floor(localD / 3600)
  const m = Math.floor((localD % 3600) / 60)
  const s = Math.floor((localD % 3600) % 60)

  const hDisplay = h > 0 ? `${h}h` : ''
  const mDisplay = m > 0 ? `${m}m` : ''
  const sDisplay = s > 0 ? `${s}s` : ''
  return `${hDisplay} ${mDisplay} ${sDisplay}`.trim()
}

export function toHHMMSS(value) {
  const secNum = parseInt(value, 10) // don't forget the second param
  let hours = Math.floor(secNum / 3600)
  let minutes = Math.floor((secNum - hours * 3600) / 60)
  let seconds = secNum - hours * 3600 - minutes * 60

  if (hours < 10) {
    hours = `0${hours}`
  }
  if (minutes < 10) {
    minutes = `0${minutes}`
  }
  if (seconds < 10) {
    seconds = `0${seconds}`
  }

  let returnValue = ''

  if (hours !== '00') {
    returnValue += `${hours}:`
  }
  returnValue += `${minutes}:${seconds}`
  return returnValue
}

export function formatKidsDuration(time) {
  const minutes = Math.floor(time / 60).toString()
  let seconds = Math.floor(time - minutes * 60).toString()
  if (seconds < 10) {
    seconds = `0${seconds}`
  }
  return `${minutes}:${seconds}`
}

export function noCacheUrl(url) {
  if (url.indexOf('nocache') !== -1) {
    return url
  }

  // if a ? doesn't exists in the url use one, otherwise use an ampersand
  const delimiter = url.indexOf('?') === -1 ? '?' : '&'
  return `${url + delimiter}nocache=${new Date().getTime()}`
}

export function secureUrl(url) {
  return url.replace('http://', 'https://')
}

export function formatContentRating(contentRating) {
  if (!contentRating) {
    return ''
  }

  // For VIZIO, we need to split out the letters so TTS reads consistently
  // Example: "TV-14" reads as "T V DASH FOURTEEN"
  // Example: "TV-MA" reads as "T V DASH MAH"

  // We want to remove every non-alphanumeric character and intersperse with spaces
  return contentRating
    .split('')
    .filter((char) => /[a-zA-Z0-9]/.test(char))
    .join(' ')
}

export function getDateObject(d) {
  const dateObject = {}
  const hour = d.getHours()
  const timezoneOffset = d.getTimezoneOffset() / 60
  const year = d.getFullYear()
  const month = d.getMonth() + 1
  const day = d.getDate()
  const fullDate = `${year.toString()}${month.toString()}${day.toString()}`
  const dayOfWeek = d.getDay()
  let nameOfDay = ''
  switch (dayOfWeek) {
    case 0:
      nameOfDay = 'Sunday'
      break

    case 1:
      nameOfDay = 'Monday'
      break

    case 2:
      nameOfDay = 'Tuesday'
      break

    case 3:
      nameOfDay = 'Wednesday'
      break

    case 4:
      nameOfDay = 'Thursday'
      break

    case 5:
      nameOfDay = 'Friday'
      break

    case 6:
      nameOfDay = 'Saturday'
      break

    default:
      nameOfDay = ''
      break
  }

  dateObject.clientHour = hour
  dateObject.clientGMTOffset = timezoneOffset
  dateObject.clientDate = fullDate
  dateObject.clientDayOfWeek = dayOfWeek
  dateObject.clientDayOfWeekName = nameOfDay

  return dateObject
}

export function stripHttpProtocol(url = '') {
  return url.replace('https://', '//').replace('http://', '//')
}

export function getFormattedOrdinals(videoResource, isShort) {
  // Shows like NewsHour show their episodes as MM/DD/YYYY

  if (videoResource?.parent?.season?.show?.display_episode_number === false) {
    const d = new Date(videoResource?.premiere_date)
    const mm = d.getMonth() + 1
    const dd = d.getDate()
    const yyyy = d.getFullYear()
    return `${mm}/${dd}/${yyyy}`
  }

  let seasonString = 'Season '
  let episodeString = 'Episode '
  if (isShort) {
    seasonString = 'S'
    episodeString = 'Ep'
  }
  switch (videoResource?.video_type) {
    case 'full_length':
      switch (videoResource.parent.resource_type) {
        case 'special':
          return 'Special'
        case 'season':
          return `${seasonString}${videoResource.parent.ordinal}`
        default:
          return `${seasonString}${videoResource.parent.season.ordinal} ${episodeString}${videoResource.parent.ordinal}`
      }
    case 'preview':
      switch (videoResource.parent.resource_type) {
        case 'special':
          return 'Preview: Special'
        case 'show':
          return 'Preview'
        case 'franchise':
          return 'Preview: Franchise'
        case 'season':
          return `Preview: ${seasonString}${videoResource.parent.ordinal}`
        default:
          return `Preview: ${seasonString}${videoResource.parent.season.ordinal} ${episodeString}${videoResource.parent.ordinal}`
      }

    case 'clip':
      switch (videoResource.parent.resource_type) {
        case 'special':
          return 'Clip: Special'
        case 'show':
          return 'Clip'
        case 'franchise':
          return 'Preview: Franchise'
        case 'season':
          return `Clip: ${seasonString}${videoResource.parent.ordinal}`
        default:
          return videoResource.parent.season.ordinal
            ? `Clip: ${seasonString}${videoResource.parent.season.ordinal} ${episodeString}${videoResource.parent.ordinal}`
            : `Clip: ${episodeString}${videoResource.parent.ordinal}`
      }

    default:
      if (videoResource?.item_type === 'skylab-collection') {
        return ''
      }
      if (videoResource?.item_type === 'season') {
        return ''
      }
      if (videoResource?.item_type === 'show') {
        return ''
      }
      return 'Unknown'
  }
}

export function getTitleAndSubtitle(videoResource) {
  let showTitle = ''
  let episodeTitle = ''
  switch (videoResource.video_type) {
    case 'full_length':
      if (videoResource.parent.resource_type === 'special') {
        showTitle = videoResource.parent.show.title
        episodeTitle = videoResource.title
      } else {
        if (videoResource.parent.season) {
          showTitle = videoResource.parent.season.show.title
        } else {
          showTitle = videoResource.parent.show.title
        }
        episodeTitle = videoResource.title
      }
      break

    case 'clip':
      if (videoResource.parent.resource_type === 'special') {
        showTitle = videoResource.parent.show.title
        episodeTitle = videoResource.title
      } else {
        if (videoResource.parent.season) {
          showTitle = videoResource.parent.season.show.title
        } else if (videoResource.parent.show) {
          showTitle = videoResource.parent.show.title
        } else {
          showTitle = videoResource.parent.title
        }
        episodeTitle = videoResource.title
      }
      break
    case 'preview':
      episodeTitle = videoResource.title
      if (videoResource.parent.resource_type === 'special') {
        showTitle = videoResource.parent.show.title
      } else if (videoResource.parent.season) {
        showTitle = videoResource.parent.season.show.title
        episodeTitle = videoResource.title
      } else if (videoResource.parent.show) {
        showTitle = videoResource.parent.show.title
      } else {
        showTitle = videoResource.parent.title
      }
      break

    default:
      if (videoResource.item_type === 'skylab-collection') {
        showTitle = ''
        episodeTitle = videoResource.title
      } else if (videoResource.item_type === 'show') {
        showTitle = videoResource.title
        episodeTitle = ''
      } else {
        return 'Unknown'
      }
      break
  }
  return { showTitle, episodeTitle }
}

function getShowPoster(images) {
  let image = ''
  if (images) {
    if (images['show-poster2x3']) {
      image = `${images['show-poster2x3']}?resize=260x390&format=webp`
    } else {
      image = ''
    }
  }

  return image
}

export function getImageUrl(asset) {
  const itemType = asset?.item_type
  const images = asset?.images
  let imageUrl = ''
  let assetMezzanine

  if (images) {
    if (itemType === 'season' && images['season-mezzanine-16x9']) {
      assetMezzanine = images['season-mezzanine-16x9']
    } else if (
      itemType === 'skylab-collection' &&
      images['collection-mezzanine-16x9']
    ) {
      assetMezzanine = images['collection-mezzanine-16x9']
    } else {
      assetMezzanine =
        images['asset-mezzanine-16x9'] || images['asset-kids-mezzanine1-16x9']
    }
  } else {
    assetMezzanine = null
  }

  switch (itemType) {
    case 'video':
    case 'season':
      if (images) {
        imageUrl = assetMezzanine
          ? `${assetMezzanine}?resize=487x274&format=webp`
          : ''
      }
      break
    case 'skylab-collection':
    case 'showcase':
      if (images) {
        imageUrl = assetMezzanine
          ? `${assetMezzanine}?resize=633x356&format=webp`
          : ''
      }
      break

    case 'show':
      imageUrl = getShowPoster(images)
      break

    default:
      if (images && assetMezzanine) {
        imageUrl = `${assetMezzanine}?crop=864x486&format=webp`
      } else {
        imageUrl = ''
      }
      break
  }
  return imageUrl
}

export function getUrl(content, slug, parentTitle) {
  if (content.item_type === 'show') {
    return `/show-details/${content.slug}`
  }
  if (content.item_type === 'video') {
    return `/video-details/${encodeURIComponent(content.url)}?availability=${
      content.availability
    }`
  }
  if (content.item_type === 'season') {
    return `/seasons/${slug}/?type=all&season=${content.ordinal}&parent=${parentTitle}`
  }
  if (content.item_type === 'skylab-collection') {
    return `/collections/${encodeURIComponent(content.url)}`
  }
  return '#'
}

function compareUserAgent(input) {
  // eslint-disable-next-line prefer-destructuring
  const userAgent = navigator.userAgent
  if (userAgent.indexOf(input) !== -1) {
    return true
  }
  return false
}

export function isRuntime2017() {
  return compareUserAgent('Tizen 3.0')
}

export function isRuntime2021() {
  return compareUserAgent('Tizen 6.0')
}

export function isRuntimeBrowser() {
  return !compareUserAgent('Tizen')
}

export function loadScript(src) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.async = true
    script.src = src
    script.onload = resolve
    script.onerror = reject
    document.body.appendChild(script)
  })
}

export function getQueryParam(queryString, param) {
  const params = queryString.substring(1).split('&')
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < params.length; i++) {
    const pair = params[i].split('=')
    if (decodeURIComponent(pair[0]) === param) {
      return decodeURIComponent(pair[1])
    }
  }
  return null
}

export function removeParam(key, sourceURL) {
  let rtn = sourceURL.split('?')[0]
  let param
  let paramsArr = []
  const queryString =
    sourceURL.indexOf('?') !== -1 ? sourceURL.split('?')[1] : ''
  if (queryString !== '') {
    paramsArr = queryString.split('&')
    for (let i = paramsArr.length - 1; i >= 0; i -= 1) {
      // eslint-disable-next-line prefer-destructuring, no-undef
      param = params_arr[i].split('=')[0]
      if (param === key) {
        paramsArr.splice(i, 1)
      }
    }
    rtn = `${rtn}?${paramsArr.join('&')}`
  }
  return rtn
}

export function getCustomDimensionsFromDate(date) {
  return {
    dimension3: date.clientHour.toString(),
    dimension4: date.clientGMTOffset.toString(),
    dimension5: date.clientDate.toString(),
    dimension6: date.clientDayOfWeek.toString(),
    dimension7: date.clientDayOfWeekName.toString(),
  }
}

export function getNightStatus() {
  const now = new Date()
  const hour = now.getHours()
  // If it's before 6am or after 8pm
  if (hour < 6 || hour > 20) {
    return true
  }
  return false
}

export function debounce(fn, time) {
  let timeout
  // eslint-disable-next-line func-names
  return function () {
    // eslint-disable-next-line prefer-rest-params
    const args = arguments
    clearTimeout(timeout)
    timeout = setTimeout(() => {
      timeout = null
      fn.apply(this, args)
    }, time)
  }
}

export function throttle(fn, time) {
  let lastTime = 0
  // eslint-disable-next-line func-names
  return function () {
    const now = Date.now()
    // eslint-disable-next-line prefer-rest-params
    const args = arguments
    if (now - lastTime >= time) {
      lastTime = now
      fn.apply(this, args)
    }
  }
}

export function getBestWhiteLogo(images) {
  return (
    images.white_single_brand_logo ||
    images.white_cobranded_logo ||
    images.white_logo
  )
}

export function getBestColorLogo(images) {
  return (
    images.color_single_brand_logo ||
    images.color_cobranded_logo ||
    images.color_logo
  )
}

export function chunkString(str, length) {
  const stringToSplit = COMPILING_FOR_COMCASTX1
    ? str.replace(/\n|\r/g, '')
    : str
  return stringToSplit.match(new RegExp(`.{1,${length}}(?=[^\\d ]|$)`, 'g'))
}

export function exitApp(isFromExitButton = false) {
  if (COMPILING_FOR_TIZEN) {
    try {
      window.tizen.application.getCurrentApplication().exit()
    } catch (error) {
      captureException(error)
    }
  } else if (COMPILING_FOR_VIZIO) {
    window.VIZIO.exitApplication()
  } else if (COMPILING_FOR_COMCASTX1) {
    import('@firebolt-js/sdk')
      .then((firebolt) => {
        const Lifecycle = firebolt?.Lifecycle
        Lifecycle.close(
          isFromExitButton
            ? Lifecycle.CloseReason.REMOTE_BUTTON
            : Lifecycle.CloseReason.USER_EXIT
        )
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Firebolt Lifecycle.close failed', err)
      })
  } else if (COMPILING_FOR_LGTV) {
    window.close()
  } else {
    window.close()
  }
}

export function getSponsorshipInfoTizen() {
  return new Promise((resolve, reject) => {
    let ppid = ''
    let latEnabled = false

    try {
      if (window.webapis.adinfo.isLATEnabled()) {
        ppid = v4()
        latEnabled = true
      } else {
        const tifa = window.webapis.adinfo.getTIFA()
        // Guard against null/undefined
        if (tifa) {
          ppid = tifa
        }
      }
    } catch (e) {
      // we probably don't need to catch here and can just return an empty string
      if (e.message.indexOf('undefined') === -1) {
        reject()
      }
    } finally {
      resolve({ ppid, latEnabled })
    }
  })
}

export function getSponsorshipInfoVIZIO() {
  return new Promise((resolve) => {
    window.VIZIO.setAdvertiserIDListener((AdvertiserID) => {
      if (AdvertiserID && AdvertiserID.IFA) {
        resolve({ ppid: AdvertiserID.IFA })
      } else {
        resolve({ ppid: '' })
      }
    })
  })
}

export function getSponsorshipInfoComcast() {
  return new Promise((resolve) => {
    resolve({ ppid: v4() })
  })
}

export function getSponsorshipInfoLG() {
  return new Promise((resolve) => {
    resolve({ ppid: v4() })
  })
}

// Returns a formatted and pluralized string for TTS to use.
export function getTTSDuration(value) {
  const secNum = parseInt(Math.round(value), 10)
  const hours = Math.floor(secNum / 3600)
  const minutes = Math.floor((secNum - hours * 3600) / 60)
  const seconds = secNum - hours * 3600 - minutes * 60

  let returnValue = ''

  if (hours > 0) {
    const hourUnit = hours === 1 ? 'Hour' : 'Hours'
    returnValue += `${hours} ${hourUnit}`
  }
  if (minutes > 0) {
    const minutesUnit = minutes === 1 ? 'Minute' : 'Minutes'
    returnValue += `${hours > 0 ? ' ' : ''}${minutes} ${minutesUnit}`
  }
  if (seconds > 0) {
    const secondsUnit = seconds === 1 ? 'Second' : 'Seconds'
    returnValue += `${
      hours > 0 || minutes > 0 ? ' ' : ''
    }${seconds} ${secondsUnit}`
  }

  return returnValue
}

export function encoreDataCheck(encoreDate) {
  const encoreUnix = Date.parse(encoreDate)
  const encoreInDays = Math.floor(encoreUnix / (24 * 60 * 60 * 1000))
  const d = new Date()
  const UnixInDays = Math.floor(d / (24 * 60 * 60 * 1000))
  const diff = UnixInDays - encoreInDays

  return diff <= 7
}

export function readKidsCarouselItem(title, isNew, video_type, duration) {
  return `${title} is focused. It is a ${isNew ? 'new ' : ''} ${
    video_type === 'Episode' ? 'full episode' : 'clip'
  }, and is ${secondsToTTS(duration)}.`
}

export function colorAndOpacityToRGBA(colorString, opacity) {
  let r = 0
  let g = 0
  let b = 0
  switch (colorString) {
    case 'white':
      r = 255
      g = 255
      b = 255
      break
    case 'black':
      r = 0
      g = 0
      b = 0
      break
    case 'red':
      r = 255
      g = 0
      b = 0
      break
    case 'green':
      r = 0
      g = 255
      b = 0
      break
    case 'blue':
      r = 0
      g = 0
      b = 255
      break
    case 'yellow':
      r = 255
      g = 255
      b = 0
      break
    case 'magenta':
      r = 255
      g = 0
      b = 255
      break
    case 'cyan':
      r = 0
      g = 255
      b = 255
      break

    default:
      break
  }
  return `rgba(${r},${g},${b},${opacity})`
}

export function getUrlSearchParameter(url, name) {
  return (
    decodeURIComponent(
      // eslint-disable-next-line prefer-template, no-useless-concat
      (new RegExp('[?|&]' + name + '=' + '([^&;]+?)(&|#|;|$)').exec(url) || [
        null,
        '',
      ])[1].replace(/\+/g, '%20')
    ) || null
  )
}

const DEFAULT_WIDTH = 1920
const DEFAULT_HEIGHT = 1080

export const getDefaultMeasurements = () => ({
  width: DEFAULT_WIDTH,
  height: DEFAULT_HEIGHT,
})

export function adjustZoom() {
  document.getElementById('root').setAttribute('style', 'zoom: 0.666')
}

export function adjustScale(element) {
  if (!element) {
    console.warn('adjustScale: No element provided')
    return
  }

  // eslint-disable-next-line no-restricted-globals
  const screenW = typeof screen !== 'undefined' ? screen.width : DEFAULT_WIDTH
  // eslint-disable-next-line no-restricted-globals
  const screenH = typeof screen !== 'undefined' ? screen.height : DEFAULT_HEIGHT

  // Use the smallest reported dimensions to detect the real physical resolution.
  // On WebOS, viewport meta "width=1920" can inflate innerWidth to 1920 even on 720p devices,
  // so we take the minimum between innerWidth and screen.width to find the true constraint.
  const viewportWidth = window.innerWidth
    ? Math.min(window.innerWidth, screenW)
    : screenW
  const viewportHeight = window.innerHeight
    ? Math.min(window.innerHeight, screenH)
    : screenH

  const shouldScale =
    viewportWidth < DEFAULT_WIDTH || viewportHeight < DEFAULT_HEIGHT

  if (shouldScale) {
    let scaleValue = 0.6667
    try {
      const scaleX = viewportWidth / DEFAULT_WIDTH
      const scaleY = viewportHeight / DEFAULT_HEIGHT
      scaleValue = Math.min(scaleX, scaleY)
    } catch (error) {
      console.warn('adjustScale: Error calculating scale, using default', error)
    }

    element.style.transform = `scale(${scaleValue})`
    element.style.transformOrigin = 'top left'
    element.style.width = `${DEFAULT_WIDTH}px`
    element.style.height = `${DEFAULT_HEIGHT}px`
  }
}

export function isPlatformScaled() {
  // Check compile-time constant first
  if (typeof PLATFORM_IS_SCALED !== 'undefined' && PLATFORM_IS_SCALED) {
    return true
  }
  // eslint-disable-next-line no-restricted-globals
  const screenW = typeof screen !== 'undefined' ? screen.width : DEFAULT_WIDTH
  // eslint-disable-next-line no-restricted-globals
  const screenH = typeof screen !== 'undefined' ? screen.height : DEFAULT_HEIGHT
  const viewportWidth = window.innerWidth
    ? Math.min(window.innerWidth, screenW)
    : screenW
  const viewportHeight = window.innerHeight
    ? Math.min(window.innerHeight, screenH)
    : screenH
  if (viewportWidth < DEFAULT_WIDTH || viewportHeight < DEFAULT_HEIGHT) {
    return true
  }
  return false
}

export function getScaledResolution() {
  return isPlatformScaled()
    ? { width: 1280, height: 720 }
    : { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT }
}

export function platformTransitionStyle(transitionString) {
  return `${PLATFORM_HAS_ANIMATIONS ? transitionString : 'none'}`
}
export function platformHasAds() {
  return !isRuntime2017()
}

export function platformStoreGet(key) {
  if (COMPILING_FOR_COMCASTX1) {
    return Cookies.get(key) || null
  }
  if (COMPILING_FOR_LGTV) {
    return webOSStorageManager.getItem(key)
  }
  return localStorage.getItem(key)
}
export function platformStoreSet(key, value) {
  if (COMPILING_FOR_COMCASTX1) {
    Cookies.set(key, value, { expires: 365 })
  } else if (COMPILING_FOR_LGTV) {
    webOSStorageManager.setItem(key, value)
  } else {
    localStorage.setItem(key, value)
  }
}
export function platformStoreDelete(key) {
  if (COMPILING_FOR_COMCASTX1) {
    Cookies.remove(key)
  } else {
    localStorage.removeItem(key)
  }
}
export function platformStoreDeleteAll(prefix) {
  if (COMPILING_FOR_COMCASTX1) {
    const allCookies = Cookies.get() // => { name: 'value' }

    // new implementation
    const localStorageAux = Object.keys(allCookies).filter(
      (localStorageElement) => localStorageElement.startsWith(prefix)
    )

    localStorageAux.forEach((key) => {
      Cookies.remove(key)
    })

    /* Object.keys(allCookies).forEach((key) => {
      if (key.startsWith(prefix)) {
        Cookies.remove(key)
      }
    }) */
  } else {
    const localStorageAux = Object.keys(localStorage).filter(
      (localStorageElement) => localStorageElement.startsWith(prefix)
    )

    localStorageAux.forEach((key) => {
      localStorage.removeItem(key)
    })
  }
}

// TODO: To be removed in UA sunset (1st July)
export function ga3Title() {
  let title = 'Browser'
  if (COMPILING_FOR_TIZEN) {
    title = 'Samsung Tizen'
  }
  if (COMPILING_FOR_VIZIO) {
    title = 'VIZIO'
  }
  if (COMPILING_FOR_COMCASTX1) {
    title = 'Comcast X1'
  }
  return title
}

export function ga4Title(distributor) {
  let title = 'Browser'
  if (COMPILING_FOR_TIZEN) {
    title = 'Samsung TV'
  }
  if (COMPILING_FOR_VIZIO) {
    title = 'VIZIO'
  }
  if (COMPILING_FOR_COMCASTX1) {
    title = distributor === 'xglobal' ? 'Xumo' : 'Comcast'
  }
  if (COMPILING_FOR_LGTV) {
    title = 'LG TV'
  }
  return title
}

export function platformTitle() {
  let title = 'Browser'
  if (COMPILING_FOR_TIZEN) {
    title = 'Samsung'
  }
  if (COMPILING_FOR_VIZIO) {
    title = 'VIZIO'
  }
  if (COMPILING_FOR_COMCASTX1) {
    title = 'Comcast'
  }
  if (COMPILING_FOR_LGTV) {
    title = 'LG'
  }
  return title
}

/**
 * Sanitizes extra parameters by converting Error objects to plain objects
 * @param {any} obj - The object to sanitize
 * @returns {any} - Sanitized object
 */
export function sanitizeErrorParams(obj) {
  if (obj instanceof Error) {
    return {
      errorName: obj.name,
      errorMessage: obj.message,
      errorStack: obj.stack,
    }
  }

  if (Array.isArray(obj)) {
    return obj.map(sanitizeErrorParams)
  }

  if (obj && typeof obj === 'object') {
    /** @type {Record<string, any>} */
    const sanitized = {}
    // TODO: review a better way to implement this method
    // eslint-disable-next-line no-restricted-syntax
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        sanitized[key] = sanitizeErrorParams(obj[key])
      }
    }
    return sanitized
  }

  return obj
}

/**
 * Converts a nested object into a Firebolt FlatMap-compatible object
 * where all values are string | number | boolean.
 * Non-primitive values are JSON-stringified.
 * @param {object} obj
 * @returns {Record<string, string | number | boolean>}
 */
export function flattenForFirebolt(obj) {
  if (!obj || typeof obj !== 'object') return {}
  const flat = {}
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key]
      if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
      ) {
        flat[key] = value
      } else if (value !== null && value !== undefined) {
        flat[key] = JSON.stringify(value)
      }
    }
  }
  return flat
}

export const ComcastErrorType = {
  network: 'network',
  media: 'media',
  restriction: 'restriction',
  entitlement: 'entitlement',
  other: 'other',
}

/**
 * This method triggers an exception to Sentry, the order of appearance is error.name,
 * errorTitle, and error.message (this is what you see on the issue list per issue).
 * Once you enter to the issue, you'll see additionalData which is the extraParam arg.
 * @param {string} errorString
 * @param {string} errorTitle
 * @param {object} extraParams
 * @param {boolean} visible
 */
export function triggerSentryError(
  errorString,
  errorTitle,
  extraParams,
  visible = false
) {
  const error = new Error(errorString)
  const sanitizedParams = extraParams ? sanitizeErrorParams(extraParams) : {}

  configureScope((scope) => scope.setTransactionName(errorTitle))
  captureException(error, { extra: sanitizedParams })
  if (COMPILING_FOR_COMCASTX1) {
    import('@firebolt-js/sdk')
      .then(async (firebolt) => {
        firebolt.Metrics.error(
          sanitizedParams.errorType || ComcastErrorType.other,
          errorTitle,
          sanitizedParams.errorMessage || errorString,
          visible,
          flattenForFirebolt(sanitizedParams),
          AGE_POLICY
        ).catch((err) => {
          // eslint-disable-next-line no-console
          console.error('Firebolt Metrics.error failed', err)
        })
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Firebolt SDK import failed', err)
      })
  }
}

/**
 *
 * Sentry uses the error obj from the parameter to get the stack.
 * @param {Error obj} error
 * @param {string} location
 */
export function trackError(error, location = '') {
  captureException(error, {
    extra: {
      errorName: error.name ?? 'An error occurred (default)',
      errorMessage: error.message ?? 'Undefined error message (default)',
      errorResponse: error.response ?? 'Undefined error response',
      location,
    },
  })
}

export function generateErrorData(error) {
  let errorStatusToDispatch = null
  let errorCode = null
  let errorMessage = null

  try {
    const parsed = JSON.parse(error.response)
    errorCode = parsed.meta.error_code
    errorMessage = parsed.meta.error_message

    if (errorCode === 404001) {
      errorStatusToDispatch = '404'
    } else {
      errorStatusToDispatch = '100'
    }
  } catch (err) {
    errorStatusToDispatch = err.name
    errorMessage = err.message
  }

  return {
    status: errorStatusToDispatch,
    code: errorCode,
    message: errorMessage,
  }
}

export function createShakaStyleSheet(
  {
    bgColor,
    bgOpacity,
    captionStyle,
    fgColor,
    fgOpacity,
    fontSize,
    fontStyle,
    textShadowValue,
    windowColor,
    windowOpacity,
  },
  type = 'vod'
) {
  const windowBackgroundColor =
    windowOpacity === 'flashing'
      ? 'unset'
      : colorAndOpacityToRGBA(windowColor, windowOpacity)
  const windowAnimationName =
    windowOpacity === 'flashing'
      ? `opacity${
          PLATFORM_HAS_FLASHING_CAPTIONS ? 'animation' : 'blink'
        }_${windowColor}`
      : 'unset'
  const backgroundColor =
    bgOpacity === 'flashing'
      ? 'unset'
      : `${colorAndOpacityToRGBA(bgColor, bgOpacity)} !important`

  const backgroundAnimationName =
    bgOpacity === 'flashing'
      ? `opacity${
          PLATFORM_HAS_FLASHING_CAPTIONS ? 'animation' : 'blink'
        }_${bgColor}`
      : 'unset'

  const foregroundAnimationName =
    fgOpacity === 'flashing'
      ? `opacity${PLATFORM_HAS_FLASHING_CAPTIONS ? 'animation' : 'blink'}_text`
      : 'unset'
  let styleSheetText
  if (type === 'live') {
    styleSheetText = `
      br {
        display: none;
      }
      span span span {
        width: unset !important;
        height: unset !important;
        padding: 10px !important;
        background-color: ${windowBackgroundColor};
        animation-name: ${windowAnimationName};
        animation-duration: 1s;
        animation-iteration-count: infinite;
      }
      span span span span {
        padding: 3px !important;
        display: block !important;
        background-color: ${backgroundColor};
        animation-name: ${backgroundAnimationName};
        animation-duration: 1s;
        animation-iteration-count: infinite;
      }
      span span span span span {
        background-color: unset !important;
        font-size: ${fontSize};
        font-family: ${fontStyle};
        font-style: ${captionStyle === 'italic' ? 'italic' : 'normal'};
        font-weight: ${captionStyle === 'bold' ? 'bold' : 'normal'};
        text-shadow: ${textShadowValue};
        color: ${fgColor};
        opacity: ${fgOpacity === 'flashing' ? 'unset' : fgOpacity};
        animation-name: ${foregroundAnimationName};
        animation-duration: 1s;
        animation-iteration-count: infinite;
      }
      span span span span span span {
        background-color: unset !important;
      }
    `
  } else {
    styleSheetText = `
      .shaka-text-container div {
        left: 18% !important;
        width: unset !important;
        padding: 3px;
        background-color: ${windowBackgroundColor};
        animation-name: ${windowAnimationName};
        animation-duration: 1s;
        animation-iteration-count: infinite;
    }
      .shaka-text-container div span:not(.shaka-text-wrapper) {
        background-color: ${backgroundColor};
        animation-name: ${backgroundAnimationName};
        animation-duration: 1s;
        animation-iteration-count: infinite;
        font-size: ${fontSize};
        font-family: ${fontStyle};
        font-style: ${captionStyle === 'italic' ? 'italic' : 'normal'};
        font-weight: ${captionStyle === 'bold' ? 'bold' : 'normal'};
        text-shadow: ${textShadowValue};
        color: ${fgColor};
        opacity: ${fgOpacity === 'flashing' ? 'unset' : fgOpacity};
        animation-name: ${foregroundAnimationName};
    }
      .shaka-text-container div span.shaka-text-wrapper {
        padding: 3px !important;
        display: block !important;
        animation-name: ${backgroundAnimationName};
        animation-duration: 1s;
        animation-iteration-count: infinite;
    }
      .shaka-text-container div span.shaka-text-wrapper span {
        background-color: ${backgroundColor};
        font-size: ${fontSize};
        font-family: ${fontStyle};
        font-style: ${captionStyle === 'italic' ? 'italic' : 'normal'};
        font-weight: ${captionStyle === 'bold' ? 'bold' : 'normal'};
        text-shadow: ${textShadowValue};
        color: ${fgColor};
        opacity: ${fgOpacity === 'flashing' ? 'unset' : fgOpacity};
        animation-name: ${foregroundAnimationName};
        animation-duration: 1s;
        animation-iteration-count: infinite;
    }
      .shaka-text-container div span.shaka-text-wrapper span span {
        background-color: unset !important;
    }
    `
  }
  const styleSheet = document.createElement('style')
  styleSheet.innerText = styleSheetText
  styleSheet.id = 'styleSheetForShaka'

  return styleSheet
}

export function textContainsHTML(htmlString) {
  const pattern = /<[^>]+>/
  return pattern.test(htmlString)
}

export function extractTextFromHTML(htmlString) {
  if (textContainsHTML(htmlString)) {
    const tempElement = document.createElement('div')
    tempElement.innerHTML = htmlString
    const text = tempElement.textContent || tempElement.innerText || htmlString
    tempElement.remove()
    return text.trim()
  }
  return htmlString
}

export const isValidDistributorForInAppDonations = (distributor) => {
  const validDistributors = ['comcast']
  return validDistributors.includes(distributor)
}

export function getComcastErrorTypeFromPlayerError(
  playerErrorCode,
  isHlsPlayer,
  data
) {
  // TODO: player engines must provide their own error mapping in the future
  const SHAKA_PLAYER_NETWORK_ERROR = 1002
  if (!isHlsPlayer && data.code === SHAKA_PLAYER_NETWORK_ERROR) {
    return ComcastErrorType.network
  }

  let comcastErrorType = ''
  switch (playerErrorCode) {
    case PLAYER_ERROR_CODES.NETWORK:
      comcastErrorType = ComcastErrorType.network
      break
    case PLAYER_ERROR_CODES.MEDIA:
      comcastErrorType = ComcastErrorType.media
      break
    default:
      comcastErrorType = ComcastErrorType.other
      break
  }
  return comcastErrorType
}

export const getPlayingListing = (channel, currentTime) =>
  channel.listings.find((listing) => {
    const startTime = new Date(listing.start_time)
    const endTime = new Date(startTime.getTime() + listing.duration * 1000)
    return currentTime >= startTime && currentTime <= endTime
  })

export const isShowPath = (url) => {
  const { pathname } = new URL(url)
  return /\/screens\/shows\/[^/]+/.test(pathname)
}

export const addQueryParam = (url, key, value) => {
  try {
    const urlObj = new URL(url)
    urlObj.searchParams.set(key, value)
    return urlObj.toString()
  } catch (e) {
    const encoded = `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    if (url.indexOf('?') !== -1) {
      return `${url}&${encoded}`
    }
    return `${url}?${encoded}`
  }
}
